import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ContractRole } from "@talentkontor/domain";
import { api } from "../../../data";
import { useStaffWorkspace } from "../../../app/session";
import { ErrorState, Loading, NoAccess } from "../../../components/States";
import { PageHead, useAction } from "../../../components/ui";
import { toast } from "../../../components/Toast";
import { t, ta, nameIn } from "../../../lib/sprache";
import { loadVorlagen } from "./daten";
import { slugKey } from "./VariablenEditor";
import { ROLE_LABEL } from "./modell";
import { buildTemplate, findSuggestions, importProblems, readDocx, readPdf, type ImportBlock, type Suggestion } from "./vertrag-import";

const MAX_BYTES = 10 * 1024 * 1024;
const KIND_TEXT = (): Record<Suggestion["kind"], string> => ({
  party_name: t("Partei", "Party"), party_address: t("Anschrift", "Address"), amount: t("Betrag", "Amount"), date: t("Datum", "Date"),
});

/**
 * Eigenen Vertrag aus Word oder PDF übernehmen (07.10.2026): Datei lesen, Platzhalter-Vorschläge bestätigen oder ändern,
 * als Entwurf anlegen. Prüfen und Aktivieren wie jede Vorlage im Editor (mit dem Häkchen zur Eigenverantwortung bei Mustern).
 */
export function VertragImport() {
  const ws = useStaffWorkspace();
  const orgId = ws.org.org_id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["contract_templates", orgId], queryFn: () => loadVorlagen(api, orgId) });
  const { run, busy, error } = useAction();
  const creator = !!ws.isCreator;
  const [datei, setDatei] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [lang, setLang] = useState<"de" | "en">("de");
  const [kindId, setKindId] = useState("");
  const [signers, setSigners] = useState<ContractRole[]>(creator ? ["brand", "agency"] : ["brand", "talent", "agency"]);
  const [blocks, setBlocks] = useState<ImportBlock[] | null>(null);
  const [vorschlaege, setVorschlaege] = useState<Suggestion[]>([]);
  const [lesefehler, setLesefehler] = useState<string | null>(null);

  const ergebnis = useMemo(() => (blocks ? buildTemplate(blocks, vorschlaege, signers, lang) : null), [blocks, vorschlaege, signers, lang]);
  const probleme = useMemo(() => (ergebnis ? importProblems(ergebnis) : []), [ergebnis]);

  if (!ws.can("edit_templates")) return <NoAccess text={t("Vorlagen legen Admins und Teammitglieder mit dem Recht „Vorlagen bearbeiten“ an.", "Templates are created by admins and team members with the “Edit templates” permission.")} />;
  if (q.error) return <ErrorState error={q.error} />;
  if (q.isPending) return <Loading />;
  const { templates, kinds } = q.data;
  const rollen: ContractRole[] = creator ? ["brand", "agency"] : ["brand", "talent", "agency"];

  async function lesen(e: FormEvent) {
    e.preventDefault();
    setLesefehler(null);
    if (!datei) return;
    if (datei.size > MAX_BYTES) { setLesefehler(t("Die Datei ist größer als 10 MB.", "The file is larger than 10 MB.")); return; }
    const bytes = new Uint8Array(await datei.arrayBuffer());
    const pdf = /\.pdf$/i.test(datei.name) || datei.type === "application/pdf";
    try {
      const b = pdf ? await readPdf(bytes) : await readDocx(bytes);
      if (!b.length) throw new Error(t("In der Datei steht kein Text.", "The file contains no text."));
      setBlocks(b);
      setVorschlaege(findSuggestions(b));
      if (!name.trim()) setName(datei.name.replace(/\.(docx|pdf)$/i, "").slice(0, 120));
    } catch (err) {
      setBlocks(null);
      setLesefehler((err as Error).message);
    }
  }

  async function anlegen() {
    if (!ergebnis || probleme.length || name.trim().length < 3) return;
    const base = slugKey(name);
    let key = base;
    for (let i = 2; templates.some((x) => x.key === key && x.language === lang); i++) key = `${base}_${i}`;
    const rows = await run("anlegen", () => api.insert<{ id: string }>("contract_templates", {
      org_id: orgId, key, name: name.trim(), language: lang, kind_id: kindId || null, status: "draft",
      body_html: ergebnis.bodyHtml, annex_html: null, variables: ergebnis.variables, required_signers: ergebnis.requiredSigners, annex_hidden_from: [],
      description: lang === "en" ? `Imported from “${datei?.name ?? ""}”` : `Übernommen aus „${datei?.name ?? ""}“`,
    }, { columns: "id" }));
    if (rows?.[0]) {
      await qc.invalidateQueries({ queryKey: ["contract_templates", orgId] });
      toast(t("Entwurf angelegt. Prüfe die Platzhalter im Text, dann aktivieren.", "Draft created. Check the placeholders in the text, then activate."));
      navigate(`/agentur/vertraege/vorlagen/${rows[0].id}`);
    }
  }

  const setzen = (id: string, choice: string) => setVorschlaege((v) => v.map((s) => (s.id === id ? { ...s, choice } : s)));
  const rolle = (r: ContractRole, on: boolean) => setSigners((s) => (on ? [...new Set([...s, r])] : s.filter((x) => x !== r)));

  return (
    <main id="main" className="content">
      <PageHead title={t("Eigenen Vertrag übernehmen", "Import your own contract")} eyebrow={t("Vertragsvorlagen", "Contract templates")}
        text={ta("Word (.docx) oder PDF hochladen. Das Portal erkennt Parteien, Beträge und Daten und schlägt Platzhalter vor; du bestätigst oder änderst jeden Vorschlag. Danach prüfst und aktivierst du die Vorlage wie jede andere.",
          "Upload Word (.docx) or PDF. The portal detects parties, amounts and dates and suggests placeholders; you confirm or change each suggestion. Then you check and activate the template like any other.",
          "Word (.docx) oder PDF hochladen. Das Portal erkennt Parteien, Beträge und Daten und schlägt Platzhalter vor; du bestätigst oder änderst jeden Vorschlag.",
          "Upload Word (.docx) or PDF. The portal detects parties, amounts and dates and suggests placeholders; you confirm or change each suggestion.")}>
        <Link className="btn" to="/agentur/vertraege/vorlagen">{t("Zu den Vorlagen", "To the templates")}</Link>
      </PageHead>

      <form className="panel stack" onSubmit={(e) => void lesen(e)}>
        <h2>{t("1. Datei", "1. File")}</h2>
        <label>{t("Vertrag als Word (.docx) oder PDF, höchstens 10 MB", "Contract as Word (.docx) or PDF, at most 10 MB")}
          <input type="file" accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => { setDatei(e.target.files?.[0] ?? null); setBlocks(null); setLesefehler(null); }} /></label>
        <small>{t("Eingescannte PDFs (Bilder) enthalten keinen Text; nimm dann die Word-Datei. Ältere .doc-Dateien bitte in Word als .docx speichern.", "Scanned PDFs (images) contain no text; use the Word file instead. Please save older .doc files as .docx in Word.")}</small>
        <div className="grid">
          <label>Name<input value={name} onChange={(e) => setName(e.target.value)} placeholder={ta("z. B. Kooperationsvertrag Agentur", "e.g. agency cooperation agreement", "z. B. Kooperationsvertrag Marke", "e.g. brand cooperation agreement")} minLength={3} maxLength={120} /></label>
          <label>{t("Sprache des Vertrags", "Language of the contract")}<select value={lang} onChange={(e) => setLang(e.target.value as "de" | "en")}>
            <option value="de">{t("Deutsch", "German")}</option><option value="en">{t("Englisch", "English")}</option></select></label>
          <label>{t("Vertragsart", "Contract type")}<select value={kindId} onChange={(e) => setKindId(e.target.value)}>
            <option value="">{t("Ohne Vertragsart", "No contract type")}</option>
            {kinds.filter((k) => !k.archived_at).map((k) => <option key={k.id} value={k.id}>{nameIn(k.name_de, k.name_en)}</option>)}
          </select></label>
        </div>
        <fieldset className="row wrap">
          <legend>{t("Wer unterschreibt?", "Who signs?")}</legend>
          {rollen.map((r) => <label key={r} className="row tk-check"><input type="checkbox" checked={signers.includes(r)} onChange={(e) => rolle(r, e.target.checked)} /><span>{ROLE_LABEL[r]}</span></label>)}
        </fieldset>
        {lesefehler && <p className="tk-error" role="alert">{lesefehler}</p>}
        <div className="row"><button type="submit" className={blocks ? "" : "primary"} disabled={!datei}>{blocks ? t("Neu lesen", "Read again") : t("Datei lesen", "Read file")}</button></div>
      </form>

      {blocks && ergebnis && (
        <>
          <section className="panel stack" aria-labelledby="vorschlaege-title">
            <h2 id="vorschlaege-title">{t("2. Platzhalter", "2. Placeholders")}</h2>
            {vorschlaege.length === 0
              ? <p>{t("Keine Parteien, Beträge oder Daten erkannt. Platzhalter setzt du nach dem Anlegen im Editor.", "No parties, amounts or dates detected. You set placeholders in the editor after creating the draft.")}</p>
              : <>
                <p>{t("Vorgeschlagen ist, was eindeutig ist. Alles andere steht auf „Nicht ersetzen“; ändere die Auswahl, wo sie nicht passt. Ersetzt wird jedes Vorkommen im Text.", "What is unambiguous is suggested. Everything else is set to “Do not replace”; change the selection where it does not fit. Every occurrence in the text is replaced.")}</p>
                <div className="tablewrap">
                  <table>
                    <thead><tr><th scope="col">{t("Art", "Type")}</th><th scope="col">{t("Im Vertrag", "In the contract")}</th><th scope="col">{t("Ersetzen durch", "Replace with")}</th></tr></thead>
                    <tbody>
                      {vorschlaege.map((s) => (
                        <tr key={s.id}>
                          <td data-label={t("Art", "Type")}>{KIND_TEXT()[s.kind]}</td>
                          <td data-label={t("Im Vertrag", "In the contract")}><span style={{ whiteSpace: "pre-line" }}>{s.text}</span>{s.occurrences > 1 && <small> · {s.occurrences}×</small>}</td>
                          <td data-label={t("Ersetzen durch", "Replace with")}>
                            <select aria-label={t(`Ersetzen: ${s.text}`, `Replace: ${s.text}`)} value={s.choice} onChange={(e) => setzen(s.id, e.target.value)}>
                              {s.options.map((o) => <option key={o.value || "nein"} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>}
          </section>

          <section className="panel stack" aria-labelledby="vorschau-title">
            <h2 id="vorschau-title">{t("3. Vorschau und anlegen", "3. Preview and create")}</h2>
            <div className="tk-import-vorschau" style={{ maxHeight: "28rem", overflow: "auto", border: "1px solid var(--line)", borderRadius: "var(--radius-md)", padding: "var(--space-4)" }}
              // Der Text ist vollständig maskiert (vertrag-import.ts, esc); nur die erlaubten Tags des Vertragstexts sind gesetzt
              dangerouslySetInnerHTML={{ __html: ergebnis.bodyHtml }} />
            {probleme.length > 0 && (
              <div className="tk-error" role="alert"><p>{t("Bevor die Vorlage angelegt werden kann:", "Before the template can be created:")}</p><ul>{probleme.map((p) => <li key={p}>{p}</li>)}</ul></div>
            )}
            <small>{t("Der Entwurf öffnet sich im Vorlagen-Editor. Dort prüfst du den Text, ergänzt bei Bedarf Platzhalter und aktivierst die Vorlage. Welche Verträge du verwendest, verantwortest du selbst.", "The draft opens in the template editor. There you check the text, add placeholders if needed and activate the template. You are responsible for the contracts you use.")}</small>
            {error && <p className="tk-error" role="alert">{error}</p>}
            <div className="row">
              <button type="button" className="primary" disabled={!!busy || probleme.length > 0 || name.trim().length < 3 || signers.length === 0} aria-busy={busy === "anlegen"} onClick={() => void anlegen()}>
                {t("Als Entwurf anlegen", "Create as draft")}</button>
              {name.trim().length < 3 && <small>{t("Bitte einen Namen mit mindestens drei Zeichen.", "Please enter a name with at least three characters.")}</small>}
            </div>
          </section>
        </>
      )}
    </main>
  );
}
